import { gql } from '@apollo/client'
import classNames from 'classnames'

import Clickable from '@web/components/Clickable'
import FollowButton from '@web/components/FollowButton'
import ProfileThumbnail from '@web/components/ProfileThumbnail'
import { ReactionNClickArea } from '@web/components/ReactionArea/reaction.constant'
import { ReactionUser_ReactionFragment, ReactionUser_UserFragment } from '@web/gql/graphql'
import { useGoTo } from '@web/hooks/useGoTo'
import useMe from '@web/hooks/useMe'
import routes from '@web/libs/constants/routes'

import styles from './ReactionUser.module.scss'

interface ReactionUserProps {
  nClickArea: ReactionNClickArea
  reaction: ReactionUser_ReactionFragment
}

const userStatMap: { key: keyof Omit<ReactionUser_UserFragment['stat'], '__typename'>; name: string }[] = [
  { key: 'reviewCount', name: '리뷰' },
  { key: 'imageCount', name: '사진' },
  { key: 'followerCount', name: '팔로워' },
  { key: 'themelistCount', name: '테마' },
]

const ReactionUser = ({ nClickArea, reaction }: ReactionUserProps) => {
  const { me } = useMe()

  const { user } = reaction
  const { isFriend, profile, stat } = user
  const { nickname, originImageUrl } = profile || {}

  const userStatForExposure = stat ? userStatMap.filter(({ key }) => !!stat[key]).slice(0, 3) : []
  const { goTo } = useGoTo()

  const handleProfileClick = () => {
    if (!user.isInDatabase) return
    goTo(routes.HOME_REVIEW, { params: { userId: user.id }, openWindow: true })
  }

  return (
    <li className={styles.list_item}>
      <Clickable className={styles.link} onClick={handleProfileClick} nClickCode={nClickArea.REACT_PROFILE}>
        <div className={styles.thumb_area}>
          <ProfileThumbnail
            size={38}
            thumbnailUrl={originImageUrl}
            isFollowing={isFriend}
            reactionType={reaction.reactionType}
          />
        </div>
        <div className={classNames(styles.info_area, { [styles.type_follow]: true })}>
          <div className={styles.name_area}>{nickname}</div>
          <div className={styles.etc_area}>
            {userStatForExposure.map(({ key, name }) => (
              <span className={styles.item} key={key}>
                {name} <em>{stat[key]}</em>
              </span>
            ))}
          </div>
        </div>
      </Clickable>
      {me?.id !== user.id && user.isInDatabase && (
        <span className={styles.btn_wrapper}>
          <FollowButton
            user={user}
            nClickCodes={{
              followRequest: nClickArea.REACT_FOL,
              cancelFollow: nClickArea.REACT_UNFOL,
              cancelFollowReqeust: nClickArea.REACT_UNFOL,
            }}
          />
        </span>
      )}
    </li>
  )
}

ReactionUser.fragments = {
  user: gql`
    fragment ReactionUser_User on User {
      id
      isFriend
      profile {
        nickname
        originImageUrl
      }
      stat {
        reviewCount
        imageCount
        followerCount
        themelistCount
      }
      isInDatabase
      ...FollowButton_User
    }
  `,
  reaction: gql`
    fragment ReactionUser_Reaction on Reaction {
      id
      reactionType
      user {
        ...ReactionUser_User
      }
    }
  `,
}

export default ReactionUser
